import { useDispatch } from "react-redux";
import requestAddToBasket from "src/features/highlighted-product/util/requestAddToBasket";
import { ProductModalErrorTypes } from "src/features/product-modal/model/ProductModalError";
import { Variant } from "src/features/product-modal/model/Variant";
import {
  addError,
  clearErrorType,
} from "src/features/combined-product/combinedProductSlice";
import { filterUnselectedVariants } from "src/features/product-modal/util/filter-unselected-variants";
import { setCombinedStatus } from "../combinedProductSlice";

interface UseCombinedAddToCardProps {
  shouldDisableBuying: boolean;
  closeModal: () => void;
  variants?: Variant[];
  pk?: number; // product variant id, for all requests.
  basePk?: number; // basePk, base product id for easy access from redux
}

export default function useCombinedAddToCard({
  shouldDisableBuying,
  closeModal,
  variants,
  pk,
  basePk,
}: UseCombinedAddToCardProps) {
  const dispatch = useDispatch();

  function setStatus(status: string) {
    dispatch(setCombinedStatus({ id: basePk, status }));
  }

  function precheckAddToCart() {
    if (shouldDisableBuying) {
      setStatus("idle");
      closeModal();
      setStatus("disabled");
      const hideBuyDisabledTimeout = setTimeout(() => {
        setStatus("idle");
        clearTimeout(hideBuyDisabledTimeout);
      }, 2000);
      return false;
    } else {
      return true;
    }
  }

  function modalErrorCheck() {
    const [hasUnselectedVariant, unselectedVariants] = filterUnselectedVariants(
      variants!
    );

    if (hasUnselectedVariant) {
      unselectedVariants.forEach((variant: Variant) => {
        dispatch(
          addError({
            productId: basePk,
            type: ProductModalErrorTypes.UnselectedVariant,
            key: variant.attributeKey,
          })
        );
      });
      return;
    } else {
      dispatch(
        clearErrorType({
          productId: basePk,
          type: ProductModalErrorTypes.UnselectedVariant,
        })
      );
    }
  }

  async function onAddToCart() {
    if (!variants) return;
    if (!precheckAddToCart()) return;
    modalErrorCheck();
    if (!pk) return;

    setStatus("loading");
    await requestAddToBasket(pk.toString());
    afterAddedToCart();
  }

  function afterAddedToCart() {
    // shop side handles this event and refreshes the basket
    document.dispatchEvent(new CustomEvent("refresh-basket"));
    // reset modal state
    setStatus("idle");
    closeModal();
    showSuccessModal();
  }

  function showSuccessModal() {
    setStatus("success");
    // hide it after 2 sec
    const hideSuccessTimeout = setTimeout(() => {
      setStatus("idle");
      clearTimeout(hideSuccessTimeout);
    }, 2000);
  }

  return {
    onAddToCart,
  };
}
